import { createPostGraphqlRequest } from '@tv2/lib-core/request';
import { defer, EMPTY, Observable, timer } from 'rxjs';
import { catchError, map, repeat, share, switchMap, takeWhile, tap } from 'rxjs/operators';
import { propertiesNotNullish } from '@tv2/lib-core/propertiesNotNullish';
import notNullish from '@tv2/lib-core/notNullish';
import { PlayUnifiedLiveTracking, PlayUnifiedLiveTrackingQuery } from './__generated__/Tracking';
import { PollResult } from './types';
import { UseTrackingProps } from '../../plugins/Tracking';
import { pollHeaders } from '../helpers';
import type { Media } from '../../types';

const DEFAULT_UPDATE_INTERVAL = 5 * 60 * 1000; // minutes * seconds * milliseconds

const liveTrackingQuery = {
  operationName: 'play_unified_live_tracking',
  query: PlayUnifiedLiveTracking,
};

/**
 * Getter function for extracting `nielsen` information from `epg` data.
 * @param   epg - Object returned from graphql endpoint.
 * @returns Array of nielsen nodes (empty if nothing was found).
 */
export function nielsen(epg: PlayUnifiedLiveTrackingQuery) {
  /* eslint-disable no-underscore-dangle */
  if (epg.tracking?.__typename !== 'LiveTracking') return [];
  if (!epg.tracking.nielsen?.nodes) return [];

  return epg.tracking.nielsen.nodes.flatMap(node => {
    if (node && propertiesNotNullish(node, 'start', 'stop')) {
      return { ...node, ad: node.ad ?? false };
    }

    return [];
  });
}

/**
 * Getter function for extracting `adobe` information from `epg` data.
 * @param   epg - Object returned from graphql endpoint.
 * @returns Array of adobe nodes (empty if nothing was found).
 */
export function adobe(epg: PlayUnifiedLiveTrackingQuery) {
  if (epg.tracking?.__typename !== 'LiveTracking') return [];

  return (epg?.tracking?.adobe?.nodes ?? []).filter(notNullish);
}

/**
 * Fetches tracking data from the `endpoint` given an entity `guid`.
 * @param options
 * @param   guid     - Entity guid
 * @param nielsenTrackingVersion
 * @returns Promise of graphql response.
 */
export function query<T>(
  options: UseTrackingProps['epgOptions'],
  guid: string,
  nielsenTrackingVersion: string | undefined
) {
  return createPostGraphqlRequest<T>(
    options.endpoint,
    liveTrackingQuery.query,
    { guid, nielsenTrackingVersion },
    liveTrackingQuery.operationName,
    pollHeaders(options.clientPlatform)
  );
}

/**
 * Given an observable of entity guids, returns an observable that, upon subscription,
 * continuously requests tracking data (based on epgUpdateInterval or every 5 minutes).
 * In case the entity is not LiveTracking, the polling will be stopped and nothing is emitted.
 * The returned observable is shared.
 * If the media is a start over, the start over epg query will be used instead.
 * @param   media$    - Observable of UnifiedPlayerMedia.
 * @param options
 * @param nielsenTrackingVersion - Set what version of nielsenTracking you wish to use (string | undefined)
 * @returns Observable emitting tracking information for live tracking.
 */
export function poll$(
  media$: Observable<Media>,
  options: UseTrackingProps['epgOptions'],
  nielsenTrackingVersion: string | undefined = undefined
): Observable<PollResult> {
  return media$.pipe(
    switchMap(media => {
      // If we are in start-over - use the transmission id as guid - it resides in metadata as mediaGuid
      const guid = media.metadata.isStartOver ? media.metadata.mediaGuid! : media.metadata.guid!;
      return liveTrackingPoll(guid, options, nielsenTrackingVersion).pipe(
        map(x => ({ liveTrackingResult: x }))
      );
    }),
    // Prevent duplicate requests in case of multiple subscribers
    share()
  );
}

function liveTrackingPoll(
  guid: string,
  options: UseTrackingProps['epgOptions'],
  nielsenTrackingVersion: string | undefined
) {
  let updateInterval = DEFAULT_UPDATE_INTERVAL;
  return defer(() =>
    query<PlayUnifiedLiveTrackingQuery>(options, guid!, nielsenTrackingVersion)
  ).pipe(
    catchError((_: unknown) => EMPTY), // retry
    tap(data => {
      if (data.tracking?.__typename !== 'LiveTracking' || data.entity === null) return;
      if (!('epgUpdateInterval' in data.entity)) return;

      updateInterval = data.entity.epgUpdateInterval
        ? data.entity.epgUpdateInterval * 1000
        : updateInterval;
    }),
    repeat({
      delay: () => {
        return timer(updateInterval);
      },
    }),
    // Stop the polling if the tracking type is not LiveTracking
    takeWhile(data => data?.tracking?.__typename === 'LiveTracking')
  );
}
